import { Component } from '@theme/component';
import { debounce, isTouch } from '@theme/utilities';
import { Swiper, Navigation, Pagination, Mousewheel, Autoplay, Thumbs, EffectFade } from '@theme/vendor';
import { getFocusableElements } from '@theme/focus';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/events';
import { syncDeferredMedia } from '@theme/media';

export class Carousel extends Component {
  /** Circle r=16 in pagination SVG; must match `_swiper.scss` stroke-dasharray. */
  static SWIPER_PAGINATION_RING = 2 * Math.PI * 16;

  requiredRefs = ['slider'];
  /** @type {() => void} */
  #navCursorMouseEnter;
  /** @type {(e: MouseEvent) => void} */
  #navCursorMouseMove;
  /** @type {() => void} */
  #navCursorMouseLeave;
  /** @type {() => void} */
  #navCursorScroll;
  /** @type {(e: MouseEvent) => void} */
  #navCursorTrackMouse;
  #onBlockSelectDesignMode = null;
  #onSectionSelectDesignMode = null;
  #onSectionLoadDesignMode = null;
  /** @type {number | null} */
  #realSlidesCount = null;
  #paginationOverridden = false;
  #slideChangeHandler = null;
  /** Set when this carousel lives inside a `dialog-component` and uses fade — used for dialog:open. */
  #dialogComponent = null;
  #autoplayObserver = null;
  #autoplayOptions = null;
  #pausedByDialog = false;
  #autoplayInViewport = false;

  #getViewportWidth() {
    return window.innerWidth;
  }

  #isDesktopViewport() {
    return this.#getViewportWidth() >= 1025;
  }

  /** @returns {boolean} */
  #isMobileViewport() {
    return this.#getViewportWidth() < 768;
  }

  static get observedAttributes() {
    return ["selected-index"];
  }


  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute("selected-index", `${index}`);
  }

  /** @param {KeyboardEvent} e */
  #paginationKeydownHandler = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (!e.target?.classList?.contains('swiper-pagination-bullet')) return;
    e.preventDefault();
    /** @type {HTMLElement} */ (e.target).click();
  };

  static get defaultModules() {
    return [Navigation, Pagination, Mousewheel, Autoplay, Thumbs, EffectFade];
  }

  get defaultOptions() {
    const defaultOptions = {
      grabCursor: true
    };

    defaultOptions.focusableElements = 'input, select, textarea, video[controls], label';

    if (this.constructor.defaultModules?.includes(Mousewheel)) {
      defaultOptions.mousewheel = {
        enabled: true,
        forceToAxis: true,
      };
    }

    if (this.showPagination) {
      const { ariaLabel, customPagination } = this.refs.sliderPagination.dataset;
      const numbers = customPagination === 'numbers';
      const progressSvg =
        `<svg viewBox="0 0 36 36"><circle class="swiper-pagination-progress" cx="18" cy="18" r="16" stroke-dashoffset="${this.constructor.SWIPER_PAGINATION_RING}"></circle></svg>`;

      defaultOptions.pagination = {
        el: this.refs.sliderPagination,
        type: this.paginationType,
        clickable: true,
        dynamicBullets: this.dynamicBullets,
        renderBullet: (index, className) => {
          const numberContent = numbers
            ? `${progressSvg}<span class="swiper-pagination-number">${index + 1}</span>`
            : '';
          return `<span class="${className}" tabindex="0" role="button" aria-label="${ariaLabel} ${index + 1}">${numberContent}</span>`;
        },
      };
    }

    const navigationOptions = this.#getNavigationOptions();
    if (navigationOptions) {
      defaultOptions.navigation = navigationOptions;
    }

    if(this.autoHeight) {
      defaultOptions.autoHeight = true;
    }

    return defaultOptions;
  }

  /**
   * Round slide widths to whole pixels so text never lands on a fractional offset — but only
   * where Swiper computes the width itself. Under `slidesPerView: 'auto'` the same flag also
   * switches the measurement to `offsetWidth` and never writes the rounded value back onto the
   * slide, so a slide whose real width is fractional drifts further out of place with every
   * slide. Numeric `slidesPerView` has no such gap: Swiper assigns the rounded width to the
   * slide, so the layout and the translate always agree.
   *
   * @param {Record<string, any>} options
   */
  #applyRoundLengths(options) {
    options.roundLengths = options.slidesPerView !== 'auto';

    const { breakpoints } = options;
    if (!breakpoints) return;

    // Swiper merges breakpoint params into the live ones rather than resetting them, so a
    // breakpoint that stays silent keeps whatever the last one set — every entry states its own.
    options.breakpoints = Object.fromEntries(
      Object.entries(breakpoints).map(([key, breakpoint]) => [
        key,
        { ...breakpoint, roundLengths: breakpoint.slidesPerView !== 'auto' },
      ])
    );
  }

  /** @type {Record<string, any> | undefined} */
  #cachedDatasetOptions;

  get datasetOptions() {
    if (this.#cachedDatasetOptions === undefined) {
      try {
        this.#cachedDatasetOptions = JSON.parse(this.getAttribute('data-options') || '{}');
      } catch (error) {
        console.warn('[carousel] invalid data-options JSON', error);
        this.#cachedDatasetOptions = {};
      }
    }
    return this.#cachedDatasetOptions;
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  get autoHeight() {
    return this.dataset.autoHeight === 'true';
  }

  get showPagination() {
    return this.refs?.sliderPagination;
  }

  get showNavigation() {
    return this.refs?.sliderNavigation;
  }

  get showCustomNavigation() {
    return this.dataset.navigationPrevClass !== '' && this.dataset.navigationNextClass !== '';
  }

  get navCenterOnImage() {
    return this.refs?.sliderNavigation?.dataset?.navCenterOnImage;
  }

  get navRefImageSelector() {
    return this.refs?.sliderNavigation?.dataset?.navRefImageSelector;
  }

  get paginationType() {
    return this.refs?.sliderPagination?.dataset?.paginationType || 'bullets';
  }

  get dynamicBullets() {
    return this.refs?.sliderPagination?.dataset?.dynamicBullets === 'true';
  }

  get enableSwipeTablet() {
    return this.dataset.enableSwipeTablet === 'true';
  }
  get enableSwipeMobile() {
    return this.dataset.enableSwipeMobile === 'true';
  }

  get navigationCursor() {
    return this.getAttribute('data-navigation-cursor') === 'true';
  }

  /**
   * Builds navigation options for Swiper
   * Supports both standard navigation buttons and custom external navigation
   * @returns {Object | null} Navigation config with nextEl/prevEl arrays or null
   */
  #getNavigationOptions() {
    const nextEl = [];
    const prevEl = [];

    // Add navigation buttons
    if (this.showNavigation) {
      const nav = this.refs?.sliderNavigation;
      const nextBtn = nav?.querySelector('.swiper-btn--next');
      const prevBtn = nav?.querySelector('.swiper-btn--prev');
      if (nextBtn) nextEl.push(nextBtn);
      if (prevBtn) prevEl.push(prevBtn);
    }

    if (this.showCustomNavigation) {
      const customNext = document.querySelector(`.${this.dataset.navigationNextClass}`);
      const customPrev = document.querySelector(`.${this.dataset.navigationPrevClass}`);
      if (customNext) nextEl.push(customNext);
      if (customPrev) prevEl.push(customPrev);
    }

    return nextEl.length > 0 && prevEl.length > 0 ? { nextEl, prevEl } : null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setupCenteredSlides();
    this._handleResize = debounce(() => this.#refreshOnResize(), 50);
    this.swiper = false;
    this.selectedIndex = this.selectedIndex;

    window.addEventListener('resize', this._handleResize);
    this.init();
    this.#renderCenteredSlidesPagination();

    const { signal } = this.abortController;
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    this.#pausedByDialog = !this.closest('.dialog[open]') && Boolean(document.querySelector('.dialog[open]'));

    this.#dialogComponent = this.closest('.dialog');
    if (this.#dialogComponent && this.datasetOptions?.effect === 'fade') {
      document.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpenForFade);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#dialogComponent && this.datasetOptions?.effect === 'fade') {
      document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpenForFade);
    }

    this.classList.remove('carousel-dialog-updated');

    this.destroySlider();
    window.removeEventListener('resize', this._handleResize);
    this._handleResize.cancel?.();

  }

  /** @param {Event} e */
  #onDialogOpenForFade = (e) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#updateSwiperAfterDialogOpen());
    });
  };

  #updateSwiperAfterDialogOpen() {
    const dialogEl = this.closest('.dialog');
    if (!dialogEl?.hasAttribute('open')) return;
    if (this.classList.contains('carousel-dialog-updated')) return;

    const sw = this.swiper;
    if (!sw || typeof sw.update !== 'function') return;

    sw.update();
    this.classList.add('carousel-dialog-updated');
  }

   /**
   * Clones slides for centered slides with loop mode.
   * Clones until total slides >= 6 so Swiper has enough to fill both sides.
   */
   #setupCenteredSlides() {
    if (!this.datasetOptions?.centeredSlides || !this.datasetOptions?.loop) return;

    const sliderWrapper = this.querySelector('[ref="sliderWrapper"]') || this.querySelector('.swiper-wrapper');
    if (!sliderWrapper) return;

    const originalSlides = Array.from(sliderWrapper.children).filter(
      slide => !slide.classList.contains('swiper-slide-duplicate') &&
               !slide.classList.contains('swiper-slide-clone')
    );
    const slidesCount = originalSlides.length;
    // Bail if out of range, or if clones already exist (avoids re-cloning on reconnect —
    // custom .swiper-slide-clone nodes aren't removed by Swiper's destroy).
    if (slidesCount < 2 || slidesCount > 4 || sliderWrapper.children.length >= 6) return;

    let totalSlides = slidesCount;
    while (totalSlides < 6) {
      const fragment = document.createDocumentFragment();
      originalSlides.forEach((slide) => {
        const revealComponents = slide.querySelectorAll('reveal-component');
        if (revealComponents.length > 0) {
          revealComponents.forEach((el) => el.setAttribute('data-skip-init', 'true'));
        }
        const highlightedTexts = slide.querySelectorAll('.highlighted-text');
        if (highlightedTexts.length > 0) {
          highlightedTexts.forEach((el) => el.setAttribute('data-skip-init', 'true'));
        }
        const clonedSlide = slide.cloneNode(true);
        clonedSlide.classList.add('swiper-slide-clone');
        clonedSlide.setAttribute('aria-hidden', 'true');
        clonedSlide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        fragment.appendChild(clonedSlide);
      });
      sliderWrapper.appendChild(fragment);
      totalSlides += slidesCount;
    }
  }

  /** Init after resize. In editor: double rAF so iframe layout is applied before reading viewport. */
  #refreshOnResize() {
    this.init();
  }

  /**
   * Initializes or destroys slider based on current breakpoint.
   * Resize: double rAF in editor so we read desktop/tablet state after layout has settled.
   */
  init() {
    if (this.shouldDestroySliderOnPortable()) {
      this.destroySlider();
    } else if (!this.swiper) {
      this.initSlider();
    }
  }

  /**
   * Re-reads `data-options` and rebuilds the slider, so callers can switch
   * options Swiper only applies at init (e.g. `effect`/`direction`) across
   * breakpoints.
   */
  reinitFromOptions() {
    this.#cachedDatasetOptions = undefined;
    this.destroySlider();
    this.init();
  }

  /**
   * Merges default options, dataset options, and custom callbacks
   * Sets up navigation height callback if navCenterOnImage is enabled
   * @returns {Object} Complete Swiper options
   */
  getOptions() {
    const options = {
      modules: this.constructor.defaultModules ?? [],
      ...(this.defaultOptions || {}),
      ...(this.datasetOptions || {}),
    };

    this.#applyRoundLengths(options);

    // Respect prefers-reduced-motion: don't autoplay (WCAG 2.2.2).
    if (options.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      delete options.autoplay;
    }
    this.#autoplayOptions = options.autoplay;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (
      this.dataset.allowTouchMoveDesktop === 'false' &&
      !isCoarsePointer
    ) {
      options.allowTouchMove = false;
    }

    if (this.dataset.allowTouchMove === 'false') {
      options.allowTouchMove = false;
    }

    if (this.navCenterOnImage) {
      const self = this;
      options.on = {
        afterInit: () => {
            self.#setNavHeight();
          },
        };
    }

    return options;
  }

  /**
   * Initializes Swiper instance with all options
   * Sets up pagination progress, focus-based navigation, and design mode support
   */
  initSlider() {
    this.refs.slider.classList.add('swiper');
    this.refs.sliderWrapper.classList.add('swiper-wrapper');
    this.swiper = new Swiper(this.refs.slider, this.getOptions());

    // Stop autoplay if not yet in viewport — IntersectionObserver will start it when in view.
    if (this.#autoplayOptions) {
      this.swiper.autoplay.stop();
    }

    this.loop = ['true', true].includes(this.swiper.params?.loop);

    if (this.showPagination) {
      this.swiper.on('autoplayTimeLeft', (_swiper, _timeLeft, percentage) => {
        const circle = this.querySelector(
          '.swiper-pagination-bullet-active .swiper-pagination-progress'
        );
        if (circle) {
          circle.setAttribute(
            'stroke-dashoffset',
            String(this.constructor.SWIPER_PAGINATION_RING * percentage)
          );
        } else {
          this.style.setProperty('--swiper-pagination-bullet-active-width', String(1 - percentage));
          this.refs.sliderPagination.classList.add('swiper-pagination--animate');
        }
      });

      this.swiper.on('slideChange', () => {
        this.refs.sliderPagination.classList.remove('swiper-pagination--animate');
        this.style.removeProperty('--swiper-pagination-bullet-active-width');
      });

      this.addEventListener('keydown', this.#paginationKeydownHandler);
    }

    const focusableElements = getFocusableElements(this.refs.slider).filter((el) => {
      const slide = el.closest('.swiper-slide');
      return !slide?.classList.contains('swiper-slide-clone') && !slide?.classList.contains('swiper-slide-duplicate');
    });
    focusableElements.forEach((element) => {
      element.addEventListener('mousedown', (e) => {
        if (e.button === 2) {
          e.preventDefault();
        }
      });
      element.addEventListener('focusin', (event) => {
        if (typeof event.target.matches === 'function') {
          let shouldHandleFocus = true;
          try {
            shouldHandleFocus = event.target.matches(':focus-visible');
          } catch (error) {
            // If :focus-visible isn't supported, fall back to default behaviour.
          }
          if (!shouldHandleFocus) return;
        }

        const slide = element.closest('.swiper-slide');
        if (!slide) return;
        if (slide.classList.contains('swiper-slide-clone') || slide.classList.contains('swiper-slide-duplicate')) return;

        // Reaching a control means the slide is already there to be seen, and
        // moving it now would pull it out from under whoever just reached it.
        if (this.#isSlideFullyVisible(slide)) return;

        const index = this.loop
          ? Number(slide.dataset.swiperSlideIndex)
          : Array.from(slide.parentElement?.children ?? []).indexOf(slide);
        if (!isNaN(index) && index !== -1) {
          this.slideTo(index);
        }
      });
    });

    if (this.refs.sliderPagination?.dataset.customPagination === 'numbers') {
      this.swiper.once('slideChange', () => this.classList.add('swiper-interacted'));
    }
    this.swiper.on('slideChangeTransitionStart', this.#handleRevealOnSlideChange);
    this.swiper.on('slideChangeTransitionEnd', () => {
      this.handleSlideChange();
    });

    if (this.#autoplayOptions) {
      this.#autoplayObserver = new IntersectionObserver(
        ([entry]) => {
          this.#autoplayInViewport = entry.isIntersecting;
          this.#syncAutoplay();
        },
        { threshold: 0.1 }
      );
      this.#autoplayObserver.observe(this);
    }

    this.selectedIndex = this.swiper.realIndex;
    this.#setupRevealComponents();
    this.#setupHeightAdapt();
    this.#updateColorScheme();

    if (Shopify.designMode) {
      if (this.#onBlockSelectDesignMode) {
        document.removeEventListener('shopify:block:select', this.#onBlockSelectDesignMode);
      }
      if (this.#onSectionSelectDesignMode) {
        document.removeEventListener('shopify:section:select', this.#onSectionSelectDesignMode);
      }

      this.#onBlockSelectDesignMode = (e) => {
        if (e.detail.sectionId != this.sectionId) return;
        const slide = e.target.closest('.swiper-slide');
        if (!slide) return;

        const index = this.loop
          ? Number(slide.dataset.swiperSlideIndex)
          : Array.from(slide.parentElement?.children ?? []).indexOf(slide);
        this.slideTo(index);
        this.#setupRevealComponents();
        this.#setupHeightAdapt();
        this.#updateColorScheme();
      };

      this.#onSectionSelectDesignMode = (e) => {
        const carouselComponent = e.target.querySelector('carousel-component');
        if (!carouselComponent) return;
        this.#setupRevealComponents();
      };

      this.#onSectionLoadDesignMode = (e) => {
        if (e.detail?.sectionId !== this.sectionId) return;
        requestAnimationFrame(() => this.#setupRevealComponents());
      };

      document.addEventListener('shopify:block:select', this.#onBlockSelectDesignMode);
      document.addEventListener('shopify:section:select', this.#onSectionSelectDesignMode);
      document.addEventListener('shopify:section:load', this.#onSectionLoadDesignMode);
    }

    if (this.navigationCursor && !isTouch() && this.showNavigation) {
      this.#setupNavCursorFollow();
      this.refs.sliderNavigation?.querySelectorAll('.swiper-btn--prev, .swiper-btn--next').forEach((btn) => {
        btn.setAttribute('tabindex', '-1');
      });
    }

    if (
      this.dataset.allowTouchMoveDesktop === 'false' &&
      this.swiper
    ) {
      this._handleAllowTouchMoveResize = debounce(() => {
        if (!this.swiper) return;
        const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
        this.swiper.allowTouchMove = isCoarsePointer;
      }, 50);
      window.addEventListener('resize', this._handleAllowTouchMoveResize);
    }
  }

  /**
   * Sets up reveal components for the active slide
   */
  #setupRevealComponents() {
    if (!this.datasetOptions?.centeredSlides || !this.datasetOptions?.loop) return;
    const activeSlide = this.refs.slider?.querySelector?.('.swiper-slide-active');
    if (!activeSlide) return;
    const revealComponents = activeSlide.querySelectorAll("reveal-component");
    if (revealComponents.length > 0) {
      revealComponents.forEach((el) => {
        el?.reinitialize?.();
      });
    }
    const highlightedTexts = activeSlide.querySelectorAll(".highlighted-text");
    if (highlightedTexts.length > 0) {
      highlightedTexts.forEach((highlightedText) => {
        highlightedText.reinitialize?.();
      });
    }
  }

  #setupHeightAdapt() {
    const setRatio = (enabled, selector, ratioVar, outVar) => {
      if (!enabled) return;
      const el = this.refs.slider?.querySelector?.(selector);
      if (!el) return;
      this.style.setProperty(outVar, getComputedStyle(el).getPropertyValue(ratioVar));
    };

    setRatio(
      this.classList.contains('carousel-height--adapt'),
      '.swiper-slide:first-child .media--height-adapt',
      '--ratio',
      '--carousel-ratio'
    );

    setRatio(
      this.classList.contains('carousel-height-mobile--adapt'),
      '.swiper-slide:first-child .media--height-mobile-adapt',
      '--ratio-mobile',
      '--carousel-ratio-mobile'
    );
  }

  #removeColorSchemeClasses(element) {
    if (!element?.classList) return;
    Array.from(element.classList)
      .filter((className) => className.startsWith('color-'))
      .forEach((className) => element.classList.remove(className));
  }

  #setColorSchemeClass(element, colorScheme) {
    if (!element?.classList || !colorScheme) return;
    this.#removeColorSchemeClasses(element);
    element.classList.add(colorScheme);
  }

  // Paints the active slide's colours onto the target. No active slide, nothing to paint.
  #updateColorScheme() {
    const activeSlide = this.refs.slider?.querySelector?.('.swiper-slide-active');
    if (!activeSlide) return;

    const selector = this.getAttribute('data-color-scheme-target');
    const target = selector
      ? this.closest(selector) ?? this
      : this;
    if (!target?.classList) return;

    const customBackground = activeSlide?.dataset?.customBackground;
    if (customBackground) {
      target.style.setProperty('--color-background', customBackground);
    } else {
      target.style.removeProperty('--color-background');
    }

    const colorScheme = activeSlide?.dataset?.colorScheme;
    if (!colorScheme) return;

    this.#setColorSchemeClass(target, colorScheme);
  }

  /**
   * Determines if slider should be destroyed on mobile/tablet.
   * @returns {boolean} True if slider should be destroyed
   */
  shouldDestroySliderOnPortable() {
    if (this.#isDesktopViewport()) return false;
    if (this.#isMobileViewport()) return this.enableSwipeMobile;
    return this.enableSwipeTablet;
  }

  /**
   * Destroys Swiper instance and cleans up classes
   * Also clears resize observer if present
   */
  destroySlider() {
    this.#autoplayObserver?.disconnect();
    this.#autoplayObserver = null;
    this.#teardownNavCursorFollow();
    if (this._handleAllowTouchMoveResize) {
      window.removeEventListener('resize', this._handleAllowTouchMoveResize);
      this._handleAllowTouchMoveResize.cancel?.();
      this._handleAllowTouchMoveResize = undefined;
    }
    if (this.swiper) {
      this.swiper.off('slideChangeTransitionStart', this.#handleRevealOnSlideChange);
    }
    if (this.showPagination) {
      this.removeEventListener('keydown', this.#paginationKeydownHandler);
    }
    this.refs.slider.classList.remove('swiper');
    this.refs.sliderWrapper.classList.remove('swiper-wrapper');
    if (this.#slideChangeHandler && this.swiper) {
      this.swiper.off('slideChange', this.#slideChangeHandler);
    }
    this.#slideChangeHandler = null;

    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.loop = false;
      this.swiper = false;
    }
    this.#realSlidesCount = null;
    this.#paginationOverridden = false;

    this.#clearResizeObserver();

    // Cleanup design mode listeners to avoid duplicate handlers after re-init.
    if (this.#onBlockSelectDesignMode) {
      document.removeEventListener('shopify:block:select', this.#onBlockSelectDesignMode);
    }
    if (this.#onSectionSelectDesignMode) {
      document.removeEventListener('shopify:section:select', this.#onSectionSelectDesignMode);
    }
    if (this.#onSectionLoadDesignMode) {
      document.removeEventListener('shopify:section:load', this.#onSectionLoadDesignMode);
    }
    this.#onBlockSelectDesignMode = null;
    this.#onSectionSelectDesignMode = null;
    this.#onSectionLoadDesignMode = null;
  }

  /**
   * Whether a slide already sits entirely inside the slider's visible box.
   * @param {Element} slide - The slide to measure
   * @returns {boolean}
   */
  #isSlideFullyVisible(slide) {
    const viewport = this.refs.slider;
    if (!viewport) return false;

    const box = viewport.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    // Sub-pixel layout leaves a slide flush against an edge a fraction over it.
    const tolerance = 1;

    return (
      rect.left >= box.left - tolerance &&
      rect.right <= box.right + tolerance &&
      rect.top >= box.top - tolerance &&
      rect.bottom <= box.bottom + tolerance
    );
  }

  /**
   * Slides to specific index
   * Uses slideToLoop for loop mode, slideTo otherwise
   * @param {number} index - Target slide index
   */
  slideTo(index) {
    if (typeof this.swiper !== 'object') return;
    const slideToMethod = this.loop ? 'slideToLoop' : 'slideTo';

    this.swiper[slideToMethod](index);
  }

  /**
   * Holds autoplay the way hovering the carousel does, so the countdown pauses where it stands
   * instead of restarting, and a slide transition can't hand it back on its own
   * @param {boolean} hold - Whether autoplay should be held
   */
  holdAutoplay(hold) {
    if (typeof this.swiper !== 'object') return;

    this.swiper.el.dispatchEvent(new PointerEvent(hold ? 'pointerenter' : 'pointerleave', { pointerType: 'mouse' }));
  }

  /**
   * Runs autoplay only while the carousel is on screen and nothing covers the page. Holding it
   * pauses rather than stops, so the countdown carries on where it was instead of starting the
   * slide over; the very first run still has to start it, since a paused autoplay must be running.
   */
  #syncAutoplay() {
    const { autoplay } = this.swiper ?? {};
    if (!autoplay) return;

    if (!this.#autoplayInViewport || this.#pausedByDialog) {
      autoplay.pause();
      return;
    }

    if (autoplay.running) autoplay.resume();
    else autoplay.start();
  }

  /**
   * Holds autoplay while an overlay covers the page. Pausing rather than stopping leaves the
   * countdown where it stands, so closing the overlay carries on instead of restarting the slide.
   */
  #handleDialogOpen = () => {
    if (!this.#autoplayOptions || this.closest('.dialog[open]')) return;

    this.#pausedByDialog = true;
    this.#syncAutoplay();
  }

  /** Picks the countdown back up once every overlay is gone. */
  #handleDialogClose = () => {
    if (!this.#pausedByDialog || document.querySelector('.dialog[open]')) return;

    this.#pausedByDialog = false;
    this.#syncAutoplay();
  }

  /**
   * Slides to next slide
   */
  slideNext() {
    if (typeof this.swiper !== 'object') return;
    this.swiper.slideNext();
  }

  /**
   * Slides to previous slide
   */
  slidePrev() {
    if (typeof this.swiper !== 'object') return;
    this.swiper.slidePrev();
  }

  /**
   * Sets navigation button height to match reference image
   * Centers navigation vertically on image, accounts for padding and gaps
   * Sets up ResizeObserver to update when image size changes
   */
  #setNavHeight() {
    if (!this.navCenterOnImage || !this.navRefImageSelector) return;
    if (!this.#isDesktopViewport()) return;

    const firstImage = this.refs.slider?.querySelector(this.navRefImageSelector);
    const navigation = this.refs.sliderNavigation;

    if (!firstImage || !navigation) return;

    const swiperComponent = firstImage.closest('.swiper-component');
    const parent = firstImage.closest('.collection-card');

    // ----- READ PHASE (no layout thrash) -----
    const parentPadding = parseFloat(
      parent?.style?.getPropertyValue('--padding-block-start') || 0
    );

    // The bleed offset compensates for bleed padding above a top-aligned image.
    // Layouts where the image is already flush to the nav anchor (e.g. a grid
    // swiper-component) opt out via data-nav-bleed-offset="false".
    const applyBleedOffset = navigation.dataset.navBleedOffset !== 'false';

    const bleedOffset =
      applyBleedOffset &&
      swiperComponent?.classList.contains('swiper-component--bleed') &&
      !swiperComponent.classList.contains('swiper-component--overflow')
        ? parseFloat(getComputedStyle(this.refs.slider).paddingBlockStart) || 0
        : 0;

    // ----- WRITE PHASE -----
    const applyStyles = (height) => {
      navigation.style.setProperty('--nav-height', `${height}px`);

      const navigationPadding = parentPadding + bleedOffset;
      if (navigationPadding) {
        navigation.style.setProperty(
          'margin-block-start',
          `${navigationPadding}px`
        );
      }
    };

    // Initial height (safe, no forced reflow)
    const rect = firstImage.getBoundingClientRect();
    applyStyles(rect.height);

    // Observe size changes
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (!this.#isDesktopViewport()) return;

        const entry = entries[0];
        if (!entry) return;

        requestAnimationFrame(() => {
          const borderBox = entry.borderBoxSize?.[0];
          applyStyles(borderBox ? borderBox.blockSize : entry.target.getBoundingClientRect().height);
        });
      });

      this.resizeObserver.observe(firstImage);
    }
  }

   /**
   * Syncs media playback state between active and inactive slides.
   * @param {Swiper} swiper - The swiper instance
   */
  handleSlideChange() {
    if (!this.swiper?.el || this.swiper.activeIndex === undefined) return;

    let slidesPerView = this.swiper.params?.slidesPerView;

    if (slidesPerView === 'auto' && typeof this.swiper.slidesPerViewDynamic === 'function') {
      slidesPerView = this.swiper.slidesPerViewDynamic();
    }
    if (slidesPerView !== 1 && !this.datasetOptions?.centeredSlides) return;

    const allDeferredMedia = this.swiper.el.querySelectorAll('deferred-media');

    allDeferredMedia.forEach(media => {
      const slide = media.closest('.swiper-slide');
      if (!slide) return;

      const slideIndex = Array.from(this.swiper.slides).indexOf(slide);
      syncDeferredMedia(media, {
        isActive: slideIndex === this.swiper.activeIndex,
        shouldAutoplay: media.hasAttribute('data-autoplay'),
      });
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if(this.getAttribute('data-refresh-reveal') !== 'true') return;
    if (name !== "selected-index" || oldValue === null || oldValue === newValue) return;

    const prevIdx = parseInt(oldValue, 10);
    const prevSlides = this.#getSlidesByIndex(prevIdx);
    const currentSlides = this.#getSlidesByIndex(parseInt(newValue, 10));

    this.#runRevealForSlides(prevIdx, prevSlides, currentSlides);
    this.#updateColorScheme();
  }

  #handleRevealOnSlideChange = (swiper) => {
    const prevIdx = this.selectedIndex;
    const currIdx = swiper.realIndex;
    this.selectedIndex = currIdx;

    const prevSlides = this.#getSlidesByIndex(prevIdx);
    const currentSlides = this.#getSlidesByIndex(currIdx);

    this.#runRevealForSlides(prevIdx, prevSlides, currentSlides);
  };

  #runRevealForSlides(prevIdx, prevSlides, currentSlides) {
    const speed = this.swiper.params?.speed ?? 0;
    prevSlides.forEach((slide) => {
      slide.querySelectorAll("reveal-component").forEach((el) => {
        setTimeout(() => {
          const nowCurrent = this.swiper?.realIndex;
          if (nowCurrent === prevIdx) return;
          el?.hideImmediately?.();
        }, speed);
      });

      slide.querySelectorAll(".highlighted-text").forEach((el) => {
        setTimeout(() => {
          const nowCurrent = this.swiper?.realIndex;
          if (nowCurrent === prevIdx) return;
          el?.hideImmediately?.();
        }, speed);
      });
    });

    let baseDelay = 0;
    if (speed > 300) {
      baseDelay = speed - 300;
    }

    currentSlides.forEach((slide) => {
      slide.querySelectorAll("reveal-component").forEach((el) => {
        el?.refreshAnimation?.(baseDelay);
      });

      slide.querySelectorAll(".highlighted-text").forEach((el) => {
        el?.refreshAnimation?.(baseDelay);
      });
    });
  }

  #getSlidesByIndex(index) {
    if (!this.swiper?.slides?.length) return [];

    if (this.loop) {
      return Array.from(this.swiper.slides).filter(
        (s) => s.getAttribute('data-swiper-slide-index') === String(index)
      );
    }

    const slide = this.swiper.slides[index];
    return slide ? [slide] : [];
  }

  #setupNavCursorFollow() {
    const nav = this.refs?.sliderNavigation;
    const prevBtn = nav?.querySelector('.swiper-btn--prev');
    const nextBtn = nav?.querySelector('.swiper-btn--next');
    if (!prevBtn || !nextBtn) return;

    const cursorAreaSelector = this.dataset.navigationCursorArea || '';

    const clearBtnStyles = (btn) => {
      btn.style.opacity = '0';
      btn.style.pointerEvents = 'none';
    };

    let rafId = 0;
    let lastDir = null;
    let isCursorFollowActive = false;
    let lastX = -9999;
    let lastY = -9999;

    const enterCursorFollow = () => {
      if (isCursorFollowActive) return;
      isCursorFollowActive = true;
      lastDir = null;
      nav.classList.add('swiper-navigation--cursor-follow');
      this.classList.add('cursor-none');
      prevBtn.style.opacity = '0';
      prevBtn.style.pointerEvents = 'none';
    };

    const leaveCursorFollow = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      lastDir = null;
      isCursorFollowActive = false;
      nav.classList.remove('swiper-navigation--cursor-follow');
      this.classList.remove('cursor-none');
      clearBtnStyles(prevBtn);
      clearBtnStyles(nextBtn);
      // Force browser to re-evaluate cursor style without waiting for next mousemove
      document.documentElement.style.cursor = 'auto';
      requestAnimationFrame(() => { document.documentElement.style.cursor = ''; });
    };

    const isOverCursorArea = (clientX, clientY) => {
      if (!cursorAreaSelector) return true;
      const mediaEls = this.querySelectorAll(cursorAreaSelector);
      for (const el of mediaEls) {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
          return true;
        }
      }
      return false;
    };

    const updateArrow = (e) => {
      const rect = this.getBoundingClientRect();
      const showPrev = e.clientX - rect.left < rect.width / 2;
      const dir = showPrev ? 'prev' : 'next';
      prevBtn.style.left = `${e.clientX}px`;
      prevBtn.style.top = `${e.clientY}px`;
      nextBtn.style.left = `${e.clientX}px`;
      nextBtn.style.top = `${e.clientY}px`;
      if (dir !== lastDir) {
        lastDir = dir;
        prevBtn.style.opacity = showPrev ? '1' : '0';
        prevBtn.style.pointerEvents = showPrev ? 'auto' : 'none';
        nextBtn.style.opacity = showPrev ? '0' : '1';
        nextBtn.style.pointerEvents = showPrev ? 'none' : 'auto';
      }
    };

    this.#navCursorMouseEnter = (e) => {
      if (cursorAreaSelector && !isOverCursorArea(e.clientX, e.clientY)) return;
      enterCursorFollow();
    };

    this.#navCursorTrackMouse = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
    };
    document.addEventListener('mousemove', this.#navCursorTrackMouse, { passive: true });

    this.#navCursorMouseMove = (e) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (cursorAreaSelector && !isOverCursorArea(e.clientX, e.clientY)) {
          if (isCursorFollowActive) leaveCursorFollow();
          return;
        }
        if (!isCursorFollowActive) enterCursorFollow();
        updateArrow(e);
      });
    };

    // Direct listeners on cursor area elements — browser fires mouseenter/mouseleave
    // when elements scroll under a stationary cursor, making this more reliable than polling.
    let scrollEndTimeout = null;
    if (cursorAreaSelector) {
      const cursorAreaEls = Array.from(this.querySelectorAll(cursorAreaSelector));
      const onAreaEnter = () => { enterCursorFollow(); updateArrow({ clientX: lastX, clientY: lastY }); };
      const onAreaLeave = () => { requestAnimationFrame(() => { if (!isOverCursorArea(lastX, lastY)) leaveCursorFollow(); }); };
      cursorAreaEls.forEach(el => {
        el.addEventListener('mouseenter', onAreaEnter);
        el.addEventListener('mouseleave', onAreaLeave);
      });
      this._navCursorAreaCleanup = () => {
        cursorAreaEls.forEach(el => {
          el.removeEventListener('mouseenter', onAreaEnter);
          el.removeEventListener('mouseleave', onAreaLeave);
        });
      };
    }

    // Fallback scroll handler for browsers that don't fire mouseenter on scroll
    this.#navCursorScroll = () => {
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = setTimeout(() => {
        if (lastX === -9999) return;
        const isOver = cursorAreaSelector
          ? isOverCursorArea(lastX, lastY)
          : (() => { const r = this.getBoundingClientRect(); return lastX >= r.left && lastX <= r.right && lastY >= r.top && lastY <= r.bottom; })();
        if (isOver && !isCursorFollowActive) {
          enterCursorFollow();
          updateArrow({ clientX: lastX, clientY: lastY });
        } else if (!isOver && isCursorFollowActive) {
          leaveCursorFollow();
        }
      }, 50);
    };

    this._navCursorScrollCleanup = () => { clearTimeout(scrollEndTimeout); };

    window.addEventListener('scroll', this.#navCursorScroll, { passive: true });

    this.#navCursorMouseLeave = () => {
      leaveCursorFollow();
    };

    this.addEventListener('mouseenter', this.#navCursorMouseEnter);
    this.addEventListener('mousemove', this.#navCursorMouseMove);
    this.addEventListener('mouseleave', this.#navCursorMouseLeave);
  }

  #teardownNavCursorFollow() {
    const nav = this.refs?.sliderNavigation;
    if (nav) {
      nav.classList.remove('swiper-navigation--cursor-follow');
    }
    this.classList.remove('cursor-none');
    if (this.#navCursorMouseEnter) {
      this.removeEventListener('mouseenter', this.#navCursorMouseEnter);
      this.removeEventListener('mousemove', this.#navCursorMouseMove);
      this.removeEventListener('mouseleave', this.#navCursorMouseLeave);
      this.#navCursorMouseEnter = undefined;
    }
    if (this.#navCursorScroll) {
      window.removeEventListener('scroll', this.#navCursorScroll);
      this.#navCursorScroll = undefined;
      this._navCursorScrollCleanup?.();
      this._navCursorScrollCleanup = null;
      this._navCursorAreaCleanup?.();
      this._navCursorAreaCleanup = null;
    }
    if (this.#navCursorTrackMouse) {
      document.removeEventListener('mousemove', this.#navCursorTrackMouse);
      this.#navCursorTrackMouse = undefined;
    }
  }

  /**
   * Overrides pagination rendering for centered slides with loop
   * Only shows bullets for real slides (not clones), updates based on realIndex
   */
  #renderCenteredSlidesPagination() {
    if (!this.swiper?.pagination || this.#paginationOverridden) return;
    if (!this.datasetOptions?.centeredSlides || !this.datasetOptions?.loop) return;

    const realSlides = this.swiper.slides.filter(
      slide => !slide.classList.contains('swiper-slide-duplicate') &&
               !slide.classList.contains('swiper-slide-clone')
    );
    this.#realSlidesCount = realSlides.length;

    if (this.#realSlidesCount < 2 || this.#realSlidesCount > 4) return;
    if (this.swiper.slides.length <= this.#realSlidesCount) return;

    const originalRenderBullet = this.swiper.params.pagination.renderBullet;
    this.swiper.params.pagination.renderBullet = (index, className) => {
      if (index >= this.#realSlidesCount) return '';
      return originalRenderBullet?.call(this.swiper.params.pagination, index, className);
    };

    this.swiper.pagination.render();

    const originalUpdate = this.swiper.pagination.update.bind(this.swiper.pagination);
    const bulletActiveClass = this.swiper.params.pagination.bulletActiveClass;

    this.swiper.pagination.update = () => {
      originalUpdate();
      if (this.#realSlidesCount) {
        const realIndex = this.swiper.realIndex % this.#realSlidesCount;
        const bullets = this.swiper.pagination.bullets;
        if (bullets) {
          bullets.forEach((bullet, index) => {
            bullet?.classList.toggle(bulletActiveClass, index === realIndex);
          });
        }
      }
    };

    this.#slideChangeHandler = () => this.swiper.pagination.update();
    this.swiper.on('slideChange', this.#slideChangeHandler);
    this.swiper.pagination.update();
    this.#paginationOverridden = true;
  }


  /**
   * Cleans up resize observer when slider is destroyed
   */
  #clearResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', Carousel);
}